// activationEmail.js — what the Control Center says about a new user's
// activation email (#73, V2-AC-02). Pure ESM, so the test gate can execute it:
// the screens only render what these return.
//
// The backend answers every create and every resend with
//   activation_email: { status: 'sent' | 'failed' | 'skipped', reason, message }
// and writes `message` in plain words naming the cause ("Email is off on this
// server: EMAIL_PROVIDER is not set."), so it is shown as written. The local
// copy below is only for an outcome that arrives without one.

/** V2-AC-02's confirmation text, shown on every successful create. */
export const CREATED = 'User created successfully.';

const FALLBACK = {
  email_off: 'Email is off on this server.',
  inactive: 'The user or their organisation is deactivated.',
  already_signed_in: 'They have already signed in, so there is nothing to activate.',
  not_allowed: "This address is not on this server's email allow-list.",
  provider_error: 'The email provider did not accept the message.',
};

/** One toast line for an outcome, or null when there is none to report — an
 *  older backend sends no `activation_email`, and saying nothing is better
 *  than guessing that an email went. */
export function emailOutcome(activation) {
  if (!activation || !activation.status) return null;
  if (activation.status === 'sent') {
    return { text: 'Activation email sent.', tone: 'ok' };
  }
  const why = activation.message || FALLBACK[activation.reason] || 'No reason was given.';
  if (activation.status === 'failed') {
    return { text: `Activation email not sent. ${why} You can resend it from the user's page.`, tone: 'err' };
  }
  return { text: `Activation email not sent. ${why}`, tone: 'warn' };
}

/** The toasts after creating a user: the confirmation, then the email line. */
export function createdToasts(result) {
  const out = [{ text: CREATED, tone: 'ok' }];
  const line = emailOutcome(result && result.activation_email);
  if (line) out.push(line);
  return out;
}

/** Offer Resend only where it can do something: an active user, in an active
 *  organisation, who has never signed in. The backend applies the same rules
 *  and answers `skipped` otherwise — this just keeps a button that can only
 *  say "skipped" off the screen. */
export function canResendActivation(user, org) {
  return Boolean(user && user.is_active && !user.sso_bound
    && !(org && org.is_active === false));
}
